import { cn } from "@devhop/ui";
import { CheckIcon } from "lucide-react";
import type { PayoutBatchWorkflowStep } from "../lib/instructor-payout-display";

const STEPS: { key: PayoutBatchWorkflowStep; label: string }[] = [
  { key: "review", label: "ກວດສອບ" },
  { key: "confirmed", label: "ຢືນຢັນບັນທຶກ" },
  { key: "paid", label: "ຈ່າຍແລ້ວ" },
];

function stepIndex(step: PayoutBatchWorkflowStep): number {
  return STEPS.findIndex((s) => s.key === step);
}

type InstructorMonthlyPayoutBatchStepperProps = {
  currentStep: PayoutBatchWorkflowStep;
  compact?: boolean;
};

export function InstructorMonthlyPayoutBatchStepper({
  currentStep,
  compact = false,
}: InstructorMonthlyPayoutBatchStepperProps) {
  const activeIndex = stepIndex(currentStep);

  return (
    <nav
      aria-label="ຂັ້ນຕອນຮອບຄ່າຈ້າງ"
      className={cn(compact ? "w-full" : undefined)}
    >
      <ol
        className={cn(
          "flex items-center",
          compact ? "gap-1 sm:gap-2" : "flex-wrap gap-2 sm:gap-3",
        )}
      >
        {STEPS.map((step, index) => {
          const isComplete = index < activeIndex;
          const isActive = index === activeIndex;

          return (
            <li key={step.key} className="flex min-w-0 items-center gap-1 sm:gap-2">
              {index > 0 ? (
                <span
                  className={cn(
                    "hidden h-px w-3 shrink-0 sm:block sm:w-6",
                    isComplete || isActive ? "bg-primary/40" : "bg-border",
                  )}
                  aria-hidden
                />
              ) : null}
              <div
                className={cn(
                  "flex min-w-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs sm:px-2.5 sm:py-1",
                  isActive && "border-primary bg-primary/10 font-medium text-primary",
                  isComplete &&
                    !isActive &&
                    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700",
                  !isActive &&
                    !isComplete &&
                    "border-border bg-muted/30 text-muted-foreground",
                )}
              >
                <span
                  className={cn(
                    "flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                    isActive && "bg-primary text-primary-foreground",
                    isComplete &&
                      !isActive &&
                      "bg-emerald-600 text-white",
                    !isActive &&
                      !isComplete &&
                      "bg-muted text-muted-foreground",
                  )}
                >
                  {isComplete && !isActive ? (
                    <CheckIcon className="size-2.5" aria-hidden />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className={cn(compact && "truncate")}>{step.label}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
